import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ZipUploadResult {
  status: string;
  files_processed: number;
  files_failed: number;
  total_files: number;
  processing_time_seconds: number;
  categorized_documents: {
    K1: any[];
    '1065': any[];
    PL: any[];
    BANK: any[];
    UNKNOWN: any[];
    ERRORS: any[];
  };
  summary: {
    total_documents: number;
    by_type: {
      K1: number;
      '1065': number;
      'P&L': number;
      'Bank Statements': number;
      Unknown: number;
    };
    errors: number;
    ready_for_wizard: boolean;
  };
}

interface ZipBatchUploadProps {
  onUploadComplete?: (result: ZipUploadResult) => void;
  onClose?: () => void;
  reportId?: string;
}

export default function ZipBatchUpload({ onUploadComplete, onClose, reportId }: ZipBatchUploadProps) {
  const { getToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState<ZipUploadResult | null>(null);
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const zipFile = files.find(f => f.name.toLowerCase().endsWith('.zip'));

    if (zipFile) {
      setSelectedFile(zipFile);
      setError('');
    } else {
      setError('Please drop a ZIP file');
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.zip')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a ZIP file');
      }
    }
  };

  const uploadZipFile = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      const url = reportId 
        ? `${BACKEND_URL}/api/v1/documents/upload-zip?report_id=${reportId}`
        : `${BACKEND_URL}/api/v1/documents/upload-zip`;

      const response = await axios.post(url, formData, config);

      setResult(response.data);
      setIsUploading(false);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
        // Auto-close modal after successful upload to show data review
        if (onClose) {
          onClose();
        }
      }
    } catch (err: any) {
      console.error('ZIP upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setResult(null);
    setError('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">📦</span>
              <div>
                <h2 className="text-2xl font-bold">Batch Upload</h2>
                <p className="text-blue-100 text-sm">Drop your ZIP → We'll handle the rest</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {!result ? (
            <>
              {/* Upload Area */}
              <div
                className={`border-3 border-dashed rounded-xl p-12 text-center transition-all ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 scale-105'
                    : selectedFile
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="space-y-4">
                    <div className="text-6xl">✅</div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{selectedFile.name}</p>
                      <p className="text-gray-600 mt-1">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={reset}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Choose different file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-6xl animate-bounce">📦</div>
                    <div>
                      <p className="text-xl font-bold text-gray-900 mb-2">
                        Drag & Drop your ZIP file here
                      </p>
                      <p className="text-gray-600">
                        or click below to browse
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="hidden"
              />

              {!selectedFile && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full mt-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Browse Files
                </button>
              )}

              {selectedFile && !isUploading && (
                <button
                  onClick={uploadZipFile}
                  className="w-full mt-4 py-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg font-bold text-lg hover:from-blue-700 hover:to-blue-700 transition shadow-lg"
                >
                  🚀 Process {selectedFile.name}
                </button>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-700">Processing...</span>
                    <span className="text-blue-600 font-bold">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-blue-600 h-full rounded-full transition-all duration-300 relative overflow-hidden"
                      style={{ width: `${uploadProgress}%` }}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    <span>Extracting, categorizing, and processing files...</span>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-semibold">⚠️ {error}</p>
                </div>
              )}

              {/* Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-gray-700">
                <p className="font-semibold mb-2">📋 What we'll process:</p>
                <ul className="space-y-1 ml-4">
                  <li>• K-1 forms (Schedule K-1)</li>
                  <li>• Tax returns (Form 1065, 1120)</li>
                  <li>• P&L statements</li>
                  <li>• Bank statements</li>
                  <li>• Supporting documents</li>
                </ul>
                <p className="mt-3 text-xs text-gray-600">
                  Max 100 files per ZIP • Max 100MB • Supported: PDF, Excel
                </p>
              </div>
            </>
          ) : (
            /* Results View */
            <div className="space-y-6">
              {/* Success Banner */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="text-5xl">🎉</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-green-900 mb-2">Processing Complete!</h3>
                    <p className="text-green-700 mb-3">
                      Successfully processed <strong>{result.files_processed}</strong> of{' '}
                      <strong>{result.total_files}</strong> files in{' '}
                      <strong>{result.processing_time_seconds}s</strong>
                    </p>
                    {result.files_failed > 0 && (
                      <p className="text-amber-700 text-sm">
                        ⚠️ {result.files_failed} file(s) could not be processed
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Summary */}
              <div className="bg-white border rounded-xl p-6">
                <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>📊</span> Documents Categorized
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(result.summary.by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">{type}</span>
                      <span className="font-bold text-blue-600">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    reset();
                    if (onClose) onClose();
                  }}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Close
                </button>
                <button
                  onClick={reset}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Upload Another ZIP
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
